import React, {useEffect, useState} from 'react';
import Services from 'services/services';
import {useServiceAuthorize, useServiceInvalidate, useServiceRefresh} from 'stores/hooks/service';
import system from 'helpers/system';
import {usePageVisibility} from 'helpers/hooks/utils';
import {useAppStore} from 'components/organisms/Providers/AppProvider/AppProvider';
import constants from 'helpers/constants';
import {useSystemSynchroniseGet} from 'services/system/system.hooks';
import {useAuthState} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import utils from 'helpers/utils';
import Debounce from 'components/organisms/Utils/Debounce/Debounce';
import {withMemo} from 'helpers/wrapper';

export const ServiceContext = React.createContext(null)
export const SynchroniseContext = React.createContext(null)

export function useServices () {
  return React.useContext(ServiceContext);
}

export function useServiceStore (path) {
  const services = React.useContext(ServiceContext);

  return services?.find(path);
}

export function useSynchronise () {
  return React.useContext(SynchroniseContext);
}

const services = new Services();

const Refresh = withMemo(({path}) => {
  useServiceRefresh(path);
});

const Service = withMemo(({service, idx}) => {
  // this renders above AuthProvider, so there is no auth context to read here yet
  const {clientId, userId, teamId} = useAuthState();

  const path = service.storePath();
  useServiceAuthorize(path);

  const synchronise = useSynchronise();
  const invalidate = useServiceInvalidate(path);

  // invalidates all that was served before the synchronise that reported the change
  const changes = synchronise?.changes?.filter((change) => service.hasEntity(change.object, false));
  useEffect(() => {
    changes?.forEach((change) => {
      if (change.removedIds?.length > 0) {
        invalidate(change.object.split('/').slice(-1)[0], synchronise.servedAt, utils.toArray(change.contextId), [], [], change.removedIds);
      }
      if (change.addedIds?.length > 0) {
        invalidate(change.object.split('/').slice(-1)[0], synchronise.servedAt, utils.toArray(change.contextId), [], change.addedIds, []);
      }
      if (change.updatedIds) {
        invalidate(change.object.split('/').slice(-1)[0], synchronise.servedAt, utils.toArray(change.contextId), change.updatedIds, [], []);
      }
    });
  }, [invalidate, changes, synchronise?.servedAt]);

  return <Debounce key={`key_${clientId ?? ''}_${userId ?? ''}_${teamId ?? ''}`}
                   timeout={constants.delay.init + (constants.delay.minimal * Math.floor(idx / 4))}>
    <Refresh path={path} />
  </Debounce>
});

const Synchronisation = withMemo(() => {
  const services = useServices();

  // above AuthProvider, same as Service above
  const {auth} = useAuthState();

  const isPageVisible = usePageVisibility();
  const [internalState, setInternalState] = useState({});

  const synchronise = useSystemSynchroniseGet({
    syncTime: internalState.syncTime,
    time: internalState.time
  }, {
    ...constants.queryOptions.runOnceFresh,
    enabled: Boolean(auth?.loggedIn) && Boolean(internalState.initialised)
  });

  useEffect(() => {
    return utils.observeTimeout(() => {
      setInternalState((current) => ({
        ...current,
        initialised: true
      }));
    }, constants.delay.init);
  }, []);

  useEffect(() => {
    const intervals = system.synchronisationInterval();
    if (!synchronise.status?.isLoading) {
      return utils.observeInterval(() => {
        setInternalState((current) => ({
          ...current,
          syncTime: synchronise.data?.data?.time,
          time: Date.now()
        }));
      }, isPageVisible ? intervals.foreground : intervals.background);
    }
  }, [isPageVisible, synchronise.data?.data?.time, synchronise.status?.isLoading]);

  useEffect(() => {
    const changes = synchronise.data?.data?.sync;
    if (changes) {
      setInternalState((current) => {
        if (current.context?.changes !== changes) {
          return {
            ...current,
            context: {
              changes: changes,
              servedAt: synchronise.meta?.servedAt
            }
          };
        } else {
          return current;
        }
      });
    }
  }, [synchronise.data?.data?.sync, synchronise.meta?.servedAt]);

  return <SynchroniseContext.Provider value={internalState.context}>
    {Object.keys(services.services).map((path, idx) => {
      const service = services.services[path];
      return <Service key={service.storePath()}
                      idx={idx}
                      service={service}/>
    })}
  </SynchroniseContext.Provider>
});

const ServiceProvider = ({children}) => {
  services.init(useAppStore());

  return <ServiceContext.Provider value={services}>
    <Synchronisation />
    {children}
  </ServiceContext.Provider>
};

ServiceProvider.propTypes = {
}

export default ServiceProvider;
